import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams, Link } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Button, Input } from "@/shared/ui";
import { LOGIN_PAGE_ROUTE, FORGOT_PASSWORD_PAGE_ROUTE } from "@/shared/utils";
import { AuthLayout } from "../components";
import { useResetPasswordMutation } from "../api";
import { mapBackendErrorToI18nKey } from "../utils/errorMapping";
import { resetPasswordSchema, type ResetPasswordFormValues } from "../validation";

function getErrorMessage(error: unknown): string {
  if (error && typeof error === "object" && "data" in error) {
    const fetchError = error as FetchBaseQueryError;

    if (fetchError.data && typeof fetchError.data === "object") {
      const errorData = fetchError.data as { error?: string; message?: string };

      if (errorData.error && typeof errorData.error === "string") {
        return mapBackendErrorToI18nKey(errorData.error);
      }

      if (errorData.message && typeof errorData.message === "string") {
        return mapBackendErrorToI18nKey(errorData.message);
      }
    }

    if (fetchError.status === "FETCH_ERROR" || fetchError.status === "PARSING_ERROR") {
      return "errors.server_unavailable";
    }

    if (typeof fetchError.status === "number" && fetchError.status >= 500) {
      return "errors.server_unavailable";
    }
  }

  return "errors.unknown";
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const { t } = useTranslation("ResetPasswordPage");
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [isSuccess, setIsSuccess] = useState(false);

  const [resetPassword, { isLoading, error }] = useResetPasswordMutation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      new_password: "",
      confirm_password: "",
    },
  });

  useEffect(() => {
    if (!token) {
      setIsSuccess(false);
    }
  }, [token]);

  async function onSubmit(data: ResetPasswordFormValues) {
    if (!token) {
      return;
    }

    try {
      await resetPassword({
        token,
        new_password: data.new_password,
      }).unwrap();

      setIsSuccess(true);
      setTimeout(() => {
        navigate(`${LOGIN_PAGE_ROUTE}?message=password_reset_success`, { replace: true });
      }, 2000);
    } catch (err) {
      void err;
    }
  }

  if (isSuccess) {
    return (
      <AuthLayout>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <div className="w-full">
          <div className="max-w-sm mx-auto mt-12 flex flex-col gap-6 text-center">
            <h1 className="text-center text-display-xs content-base-primary">{t("title")}</h1>
            <p className="text-body-regular-md content-base-secondary">{t("success.message")}</p>
            <p className="text-body-regular-sm content-base-secondary">{t("success.redirecting")}</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (!token) {
    return (
      <AuthLayout>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <div className="w-full">
          <div className="max-w-sm mx-auto mt-12 flex flex-col gap-6 text-center">
            <h1 className="text-center text-display-xs content-base-primary">{t("title")}</h1>
            <p className="text-body-regular-md content-action-negative">{t("errors.invalid_token")}</p>
            <Link to={FORGOT_PASSWORD_PAGE_ROUTE}>
              <Button variant="primary">{t("errors.requestNewToken")}</Button>
            </Link>
            <Link to={LOGIN_PAGE_ROUTE} className="text-body-regular-sm underline content-base-primary">
              {t("backToLogin")}
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <title>{t("meta.title")}</title>
      <meta name="description" content={t("meta.description")} />
      <div className="w-full">
        <form onSubmit={handleSubmit(onSubmit)} className="max-w-sm mx-auto mt-12 flex flex-col gap-6">
          <h1 className="text-center text-display-xs content-base-primary">{t("title")}</h1>
          <Input
            type="password"
            label={t("newPassword.label")}
            placeholder={t("newPassword.placeholder")}
            autoComplete="new-password"
            {...register("new_password")}
            error={errors.new_password?.message ? t(errors.new_password.message) : undefined}
          />

          <Input
            type="password"
            label={t("confirmPassword.label")}
            placeholder={t("confirmPassword.placeholder")}
            autoComplete="new-password"
            {...register("confirm_password")}
            error={errors.confirm_password?.message ? t(errors.confirm_password.message) : undefined}
          />

          {error && !isLoading && <p className="content-action-negative">{t(getErrorMessage(error))}</p>}
          <Button size="lg" variant="primary" disabled={isLoading} type="submit">
            {isLoading ? t("loading") : t("button")}
          </Button>
          <Link to={LOGIN_PAGE_ROUTE} className="text-center text-body-regular-sm underline content-base-primary">
            {t("backToLogin")}
          </Link>
        </form>
      </div>
    </AuthLayout>
  );
}
